import {
  LoginForm,
  LoginMainFooterBandItem,
  LoginMainFooterLinksItem,
  LoginPage,
} from '@patternfly/react-core'
import { ExclamationCircleIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import bgImgSrcSm from '@app/assets/images/intake-login-bg-sm.png'
import bgImgSrcXSm from '@app/assets/images/intake-login-bg-xsm.png'
import bgImgSrc from '@app/assets/images/intake-login-bg.png'
import logoImgSrc from '@app/assets/images/intake-logo-landing.png'
import type { AppState } from '@app/state'
import * as Auth from '@app/state/authenticate'
import { formatVersionInfo } from '@app/state/config/config.helpers'
import type { IAppVersionInfo } from '@app/state/config/interfaces'

const VersionDiv = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  color: white;

  > * {
    margin-right: 0.5rem;

    &:last-child {
      margin-right: 0;
    }
  }
`

export const LoginScreen = () => {
  const dispatch = useDispatch()

  const [showErrorText, setShowErrorText] = useState<boolean>(false)
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [isValidEmail, setIsValidEmail] = useState<boolean>(true)
  const [isValidPassword, setIsValidPassword] = useState<boolean>(true)
  const [isFormComplete, setIsFormComplete] = useState<boolean>(true)

  const clientVersionInfo = useSelector<AppState, IAppVersionInfo>(
    (state) => state.config.client?.versionInfo,
    shallowEqual,
  )

  const authStatus = useSelector<AppState, Auth.AuthActionStatus>(
    (state) => state.auth.authPasswordStatus,
  )

  useEffect(() => {
    const isComplete =
      email != null &&
      email.trim() !== '' &&
      password != null &&
      password.trim() !== ''
    setIsFormComplete(isComplete)
  }, [email, password])

  useEffect(() => {
    if (authStatus === Auth.AuthActionStatus.Failed) {
      setShowErrorText(true)
      setIsValidPassword(false)
      setIsValidEmail(false)
    }
  }, [authStatus])

  const handleOAuth = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      dispatch(Auth.actions.oauth())
    },
    [dispatch],
  )

  const handleEmailUpdate = useCallback((newEmail) => {
    const isEmailValid = newEmail != null && newEmail.trim() !== ''
    setEmail(newEmail)
    setIsValidEmail(isEmailValid)
    setIsValidPassword(true)
  }, [])

  const handlePasswordUpdate = useCallback((newPassword) => {
    const isPasswordValid = newPassword != null && newPassword.trim() !== ''
    setPassword(newPassword)
    setIsValidPassword(isPasswordValid)
    setIsValidEmail(true)
  }, [])

  const handleLoginButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault()
      dispatch(Auth.actions.credentials(email, password))
    },
    [dispatch, email, password],
  )

  const invalidCredentialsText = (
    <>
      <ExclamationCircleIcon /> There was an error with your Email/Password
      combination. Please try again.
    </>
  )

  const forgotCredentials = (
    <LoginMainFooterBandItem>
      <NavLink to={'/forgot-password'}>Forgot your password?</NavLink>
    </LoginMainFooterBandItem>
  )

  const OAuth2LoginContent = (
    <>
      <LoginMainFooterLinksItem
        onClick={handleOAuth}
        linkComponentProps={{ 'aria-label': 'Sign in with Network Account' }}
      >
        Sign in with Network Account
      </LoginMainFooterLinksItem>
    </>
  )

  const images = {
    lg: bgImgSrc,
    sm: bgImgSrcSm,
    sm2x: bgImgSrcSm,
    xs: bgImgSrcXSm,
    xs2x: bgImgSrcXSm,
  }

  return (
    <React.Fragment>
      <LoginPage
        brandImgSrc={logoImgSrc}
        loginTitle={'Sign in to your account'}
        loginSubtitle={'Please use the email associated with your account'}
        forgotCredentials={forgotCredentials}
        socialMediaLoginContent={OAuth2LoginContent}
        backgroundImgSrc={images}
      >
        <LoginForm
          showHelperText={showErrorText}
          helperText={invalidCredentialsText}
          loginButtonLabel="Sign in"
          usernameLabel="Email address"
          usernameValue={email}
          isValidUsername={isValidEmail}
          passwordLabel="Password"
          passwordValue={password}
          isValidPassword={isValidPassword}
          onChangeUsername={handleEmailUpdate}
          onChangePassword={handlePasswordUpdate}
          onLoginButtonClick={handleLoginButtonClick}
          isLoginButtonDisabled={
            !isFormComplete || !isValidEmail || !isValidPassword
          }
        />
      </LoginPage>
      <VersionDiv>
        <div>intakeDI</div>
        {clientVersionInfo ? (
          <div>{formatVersionInfo(clientVersionInfo)}</div>
        ) : null}
      </VersionDiv>
    </React.Fragment>
  )
}

export default LoginScreen
